import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDynamicConfig } from "@statsig/react-bindings";
import { SocketChannelNamespaceType } from "@/constant/export/SocketChannelNamespaceType";
import { getChannel } from "@/utils/socketManager/socketManager";
import { fetchSegmentsByMarketId } from "@/store/slices/segments/segments.thunk";
import { AIAssistantEventType } from "../../constants/AIAssistantEventType";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import { createAssistantMessageId } from "../../utils/clientIdentity";
import { emitAssistantToast } from "../../utils/assistantToast";
import {
  addMessage,
  appendToMessage,
  assistantResponding,
  clearActiveRequest,
  removeMessageById,
  setActiveRequestHasContent,
  setHistoryMetadata,
  setTaskStatusMessage,
  userTyping,
} from "../../state/actions";

const TYPE_CHUNK_SIZE = 20;
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};
const REQUEST_SCOPED_EVENT_TYPES = new Set([
  "stream_start",
  "stream_end",
  "text",
  "data",
  "assumption",
  "sources",
  "chart",
  "table",
  "history_metadata",
  "link",
  "custom",
  "custom_type",
  "error",
  "terminal",
]);
const waitForNextFrame = () =>
  new Promise((resolve) => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });

const buildSelectedMarketIds = (userSelection) => {
  const markets = Array.isArray(userSelection?.markets)
    ? userSelection.markets
    : EMPTY_ARRAY;
  if (markets.length > 0) {
    return markets.map((id) => Number(id));
  }
  const singleMarket = userSelection?.market;
  if (singleMarket !== null && singleMarket !== undefined) {
    return [Number(singleMarket)];
  }
  return EMPTY_ARRAY;
};

const normalizeMarketIds = (marketIds) =>
  Array.from(
    new Set((marketIds || []).map((id) => Number(id)).filter(Number.isFinite)),
  ).sort((a, b) => a - b);

const getEventMarketIds = (rawPayload = {}) =>
  normalizeMarketIds(
    rawPayload.marketIds ??
      rawPayload?.payload?.marketIds ??
      rawPayload.geoIds ??
      rawPayload?.payload?.geoIds,
  );

const resolveMarketRefresh = (eventMarketIds, selectedMarketIds) => {
  if (!eventMarketIds.length) {
    return { marketIdsToRefresh: [], shouldRefresh: false };
  }
  const normalizedSelected = normalizeMarketIds(selectedMarketIds);
  if (
    normalizedSelected.length > 0 &&
    !eventMarketIds.some((id) => normalizedSelected.includes(id))
  ) {
    return { marketIdsToRefresh: [], shouldRefresh: false };
  }
  return {
    marketIdsToRefresh:
      normalizedSelected.length > 0 ? normalizedSelected : eventMarketIds,
    shouldRefresh: true,
  };
};

const parseStatusMessage = (message) => {
  let payload = message?.data;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch (err) {
      return null;
    }
  }
  if (!payload) return null;
  return typeof payload === "object" ? payload : { taskMessage: String(payload) };
};

const buildStatusUpdate = (statusPayload) => ({
  type: statusPayload.type || "task_status",
  stage: statusPayload.stage,
  taskTitle: statusPayload.taskTitle || statusPayload.stage || "Thinking...",
  taskMessage:
    statusPayload.taskMessage ||
    statusPayload.message ||
    statusPayload.text ||
    "",
  message_id: statusPayload.message_id || statusPayload.messageId,
});

const parseAIMessageData = (message) => {
  let rawPayload = message?.data;
  if (typeof rawPayload === "string") {
    try {
      rawPayload = JSON.parse(rawPayload);
    } catch (err) {
      return null;
    }
  }
  if (!rawPayload || typeof rawPayload !== "object") return null;
  return normalizePayload(rawPayload);
};

const shouldSkipAIMessage = (payload, conversationId, shouldIgnoreSequence) => {
  if (!payload?.type) return true;
  if (conversationId && payload.conversation_id && payload.conversation_id !== conversationId) {
    return true;
  }
  if (shouldIgnoreSequence(payload.message_id, payload.sequence)) {
    return true;
  }
  return false;
};

const runHandler = async (handler, payload) => {
  if (!handler) return;
  const result = handler(payload);
  if (result && typeof result.then === "function") {
    try {
      await result;
    } catch (err) {
      console.error("AI socket handler failed", err);
    }
  }
};

const parseMarkdownLink = (input, startIndex) => {
  const closeBracket = input.indexOf("]", startIndex + 1);
  if (closeBracket === -1) return { status: "pending" };
  if (input[closeBracket + 1] !== "(") return { status: "none" };
  const closeParen = input.indexOf(")", closeBracket + 2);
  if (closeParen === -1) return { status: "pending" };
  return {
    status: "match",
    text: input.slice(startIndex, closeParen + 1),
    end: closeParen + 1,
  };
};

const parseUrl = (input, startIndex) => {
  const slice = input.slice(startIndex);
  const match = slice.match(/^https?:\/\/[^\s]+/i);
  if (!match) return { status: "none" };
  const urlText = match[0];
  const end = startIndex + urlText.length;
  if (end === input.length) return { status: "pending", text: urlText, end };
  return { status: "match", text: urlText, end };
};

const pushSegment = (segments, text, atomic) => {
  if (!text) return;
  segments.push({ text, atomic });
};

const handlePendingRemainder = (segments, input, lastEmit, cursor, isFinal) => {
  if (isFinal) return null;
  pushSegment(segments, input.slice(lastEmit, cursor), false);
  return { remainder: input.slice(cursor) };
};

const consumeMarkdownAtCursor = (input, cursor, lastEmit, segments, isFinal) => {
  if (input[cursor] !== "[") return null;
  const parsed = parseMarkdownLink(input, cursor);
  if (parsed.status === "match") {
    pushSegment(segments, input.slice(lastEmit, cursor), false);
    pushSegment(segments, parsed.text, true);
    return { cursor: parsed.end, lastEmit: parsed.end };
  }
  if (parsed.status === "pending") {
    return handlePendingRemainder(segments, input, lastEmit, cursor, isFinal);
  }
  return null;
};

const consumeUrlAtCursor = (input, cursor, lastEmit, segments, isFinal) => {
  if (!(input.startsWith("http://", cursor) || input.startsWith("https://", cursor))) {
    return null;
  }
  const parsed = parseUrl(input, cursor);
  if (parsed.status === "match") {
    pushSegment(segments, input.slice(lastEmit, cursor), false);
    pushSegment(segments, parsed.text, true);
    return { cursor: parsed.end, lastEmit: parsed.end };
  }
  if (parsed.status === "pending") {
    const pending = handlePendingRemainder(segments, input, lastEmit, cursor, isFinal);
    if (pending) return pending;
    pushSegment(segments, input.slice(lastEmit, cursor), false);
    pushSegment(segments, parsed.text, true);
    return { cursor: parsed.end, lastEmit: parsed.end };
  }
  return null;
};

const splitStreamingText = (input, { isFinal = false } = {}) => {
  const segments = [];
  let cursor = 0;
  let lastEmit = 0;

  while (cursor < input.length) {
    const markdownResult = consumeMarkdownAtCursor(
      input,
      cursor,
      lastEmit,
      segments,
      isFinal,
    );
    if (markdownResult) {
      if (markdownResult.remainder !== undefined) {
        return { segments, remainder: markdownResult.remainder };
      }
      cursor = markdownResult.cursor;
      lastEmit = markdownResult.lastEmit;
      continue;
    }

    const urlResult = consumeUrlAtCursor(
      input,
      cursor,
      lastEmit,
      segments,
      isFinal,
    );
    if (urlResult) {
      if (urlResult.remainder !== undefined) {
        return { segments, remainder: urlResult.remainder };
      }
      cursor = urlResult.cursor;
      lastEmit = urlResult.lastEmit;
      continue;
    }

    cursor += 1;
  }

  pushSegment(segments, input.slice(lastEmit), false);
  return { segments, remainder: "" };
};

const normalizePayload = (payload) => {
  const basePayload = payload?.payload && typeof payload.payload === "object"
    ? payload.payload
    : {};
  const pick = (...keys) => {
    for (const key of keys) {
      if (basePayload[key] !== undefined) return basePayload[key];
      if (payload?.[key] !== undefined) return payload[key];
    }
    return undefined;
  };

  return {
    type: payload?.type,
    message_id: pick("message_id", "messageId"),
    conversation_id: pick("conversation_id", "conversationId"),
    sequence: payload?.sequence,
    text: pick("text", "data", "message"),
    taskTitle: pick("taskTitle", "title"),
    taskMessage: pick("taskMessage", "message", "text"),
    assumption: pick("assumption"),
    sources: pick("sources"),
    chartConfig: pick("chartConfig"),
    tableConfig: pick("tableConfig"),
    metadata: pick("metadata"),
    link: pick("link"),
    title: pick("title"),
    description: pick("description"),
    outcome: pick("outcome"),
    code: pick("code"),
    had_content: pick("had_content", "hadContent"),
    should_logout: pick("should_logout", "shouldLogout"),
    terminal: pick("terminal"),
    state: pick("state"),
    raw: payload,
  };
};

const AssistantSocketListener = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const config = useDynamicConfig(
    AI_ASSISTANT_CONSTANTS.DYNAMIC_CONFIGS.AI_ASSISTANT_CONFIGS,
  );
  const shouldAutoOpenNavigationLinks = config.get(
    AI_ASSISTANT_CONSTANTS.DYNAMIC_CONFIGS.AUTO_OPEN_NAVIGATION_LINKS,
    false,
  );
  const { ablyClient, clientId, conversationId } = useAssistantAbly();
  const userSelection = useSelector((state) => state?.userSelection);
  const selectedMarketIds = useMemo(() => {
    return buildSelectedMarketIds(userSelection);
  }, [userSelection?.markets, userSelection?.market]);
  const activeRequestId = useSelector((state) => state?.chat?.activeRequestId || null);
  const marketsById =
    useSelector((state) => state?.activeGroup?.markets?.markets?.entities) ||
    EMPTY_OBJECT;

  const messageTextSegmentsRef = useRef(new Map());
  const pendingTextByMessageRef = useRef(new Map());
  const typingQueueRef = useRef([]);
  const typingInProgressRef = useRef(false);
  const lastSequenceByMessageRef = useRef(new Map());
  const streamStateRef = useRef(new Map());
  const closedStreamIdsRef = useRef(new Set());
  const lastConnectionStateRef = useRef(null);
  const contentClosedStatusRef = useRef(new Set());

  useEffect(() => {
    dispatch(assistantResponding(Boolean(activeRequestId)));
  }, [activeRequestId, dispatch]);

  useEffect(() => {
    messageTextSegmentsRef.current.clear();
    pendingTextByMessageRef.current.clear();
    lastSequenceByMessageRef.current.clear();
    streamStateRef.current.clear();
    closedStreamIdsRef.current.clear();
    contentClosedStatusRef.current.clear();
    typingQueueRef.current = [];
    typingInProgressRef.current = false;
    lastConnectionStateRef.current = null;
    dispatch(clearActiveRequest());
    dispatch(setTaskStatusMessage(null));
    dispatch(assistantResponding(false));
  }, [conversationId, dispatch]);

  const getActiveRequestId = () => store?.getState?.()?.chat?.activeRequestId || null;
  const isActiveRequestMessage = (messageId) => {
    const currentActiveRequestId = getActiveRequestId();
    return Boolean(
      messageId && currentActiveRequestId && messageId === currentActiveRequestId,
    );
  };

  const ensureStreamState = (messageId) => {
    if (!messageId) return null;
    const existing = streamStateRef.current.get(messageId);
    if (existing) return existing;
    const initial = { hasContent: false, hasError: false };
    streamStateRef.current.set(messageId, initial);
    return initial;
  };

  const markStreamState = (messageId, updates = {}) => {
    if (!messageId) return;
    const current = ensureStreamState(messageId);
    streamStateRef.current.set(messageId, { ...current, ...updates });
  };

  const pushAssistantErrorMessage = (message, preferredMessageId) => {
    const errorText =
      (typeof message === "string" && message.trim()) ||
      AI_ASSISTANT_CONSTANTS.ERRORS.SERVER_ERROR;
    const preferredId =
      typeof preferredMessageId === "string" && preferredMessageId.trim()
        ? preferredMessageId.trim()
        : null;
    const existingMessageForPreferredId = preferredId
      ? (store?.getState?.()?.chat?.messages || EMPTY_ARRAY).find(
          (msg) => msg?.message_id === preferredId,
        )
      : null;
    const errorMessageId =
      preferredId &&
      (!existingMessageForPreferredId ||
        existingMessageForPreferredId?.message_type ===
          AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.ERROR)
        ? preferredId
        : createAssistantMessageId();
    dispatch(removeMessageById(errorMessageId));
    dispatch(
      addMessage({
        type: AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_AGENT,
        message_type: AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.ERROR,
        message_id: errorMessageId,
        payload: {
          error: errorText,
          text: errorText,
          message_id: errorMessageId,
        },
      }),
    );
  };
  const markRenderableContentReceived = (messageId) => {
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = ensureStreamState(messageId);
    if (streamState?.hasContent) return;
    markStreamState(messageId, { hasContent: true });
    dispatch(setActiveRequestHasContent(true));
    if (contentClosedStatusRef.current.has(messageId)) return;
    contentClosedStatusRef.current.add(messageId);
    dispatch(setTaskStatusMessage(null));
  };
  const releaseStreamResources = (messageId) => {
    pendingTextByMessageRef.current.delete(messageId);
    messageTextSegmentsRef.current.delete(messageId);
    lastSequenceByMessageRef.current.delete(messageId);
    contentClosedStatusRef.current.delete(messageId);
  };
  const ensureFirstTextSegment = (messageId) => {
    if (!messageId) return;
    if (messageTextSegmentsRef.current.has(messageId)) return;
    createNewTextSegment(messageId);
  };

  const createNewTextSegment = (messageId) => {
    if (!messageId) return;
    const currentCount = messageTextSegmentsRef.current.get(messageId) || 0;
    messageTextSegmentsRef.current.set(messageId, currentCount + 1);
    dispatch(
      addMessage({
        type: "Harris",
        message_type: "text",
        message_id: messageId,
        payload: { text: "", message_id: messageId },
      }),
    );
  };

  const enqueueTextAppend = async (messageId, text, { isFinal = false } = {}) => {
    if (!messageId) return;
    const pending = pendingTextByMessageRef.current.get(messageId) || "";
    const combined = `${pending}${text || ""}`;
    if (!combined && !isFinal) return;

    const { segments, remainder } = splitStreamingText(combined, { isFinal });
    pendingTextByMessageRef.current.set(messageId, remainder);
    if (!segments.length) return;

    typingQueueRef.current.push(
      ...segments.map((segment) => ({ ...segment, messageId })),
    );

    if (typingInProgressRef.current) return;
    typingInProgressRef.current = true;
    const typeOut = async (segment) => {
      if (!segment?.text) return;
      if (segment.atomic) {
        dispatch(appendToMessage({ message_id: segment.messageId, textChunk: segment.text }));
        if (segment.text.trim().length > 0) {
          markRenderableContentReceived(segment.messageId);
        }
        if (typingQueueRef.current.length > 0) {
          await waitForNextFrame();
        }
        return;
      }
      for (let idx = 0; idx < segment.text.length; idx += TYPE_CHUNK_SIZE) {
        const nextSlice = segment.text.slice(idx, idx + TYPE_CHUNK_SIZE);
        dispatch(appendToMessage({ message_id: segment.messageId, textChunk: nextSlice }));
        if (nextSlice.trim().length > 0) {
          markRenderableContentReceived(segment.messageId);
        }
        const hasMoreSlices = idx + TYPE_CHUNK_SIZE < segment.text.length;
        if (!hasMoreSlices && typingQueueRef.current.length === 0) continue;
        // eslint-disable-next-line no-await-in-loop
        await waitForNextFrame();
      }
    };

    try {
      while (typingQueueRef.current.length) {
        const next = typingQueueRef.current.shift();
        // eslint-disable-next-line no-await-in-loop
        await typeOut(next);
      }
    } finally {
      typingInProgressRef.current = false;
    }
  };

  const finalizeRequest = async (messageId) => {
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = ensureStreamState(messageId);
    if (streamState?.terminalHandled) return;

    markStreamState(messageId, {
      closed: true,
      terminalHandled: true,
      hasError: false,
      hasContent: Boolean(streamState?.hasContent),
    });

    await enqueueTextAppend(messageId, "", { isFinal: true });
    releaseStreamResources(messageId);
    closedStreamIdsRef.current.add(messageId);
    streamStateRef.current.delete(messageId);

    dispatch(userTyping(false));
    dispatch(setTaskStatusMessage(null));
    dispatch(assistantResponding(false));
    dispatch(clearActiveRequest());
  };

  const shouldIgnoreSequence = (messageId, sequence) => {
    const numericSequence = Number(sequence);
    if (!messageId || !Number.isFinite(numericSequence)) return false;
    const lastSequence = lastSequenceByMessageRef.current.get(messageId);
    if (lastSequence !== undefined && numericSequence <= lastSequence) {
      return true;
    }
    lastSequenceByMessageRef.current.set(messageId, numericSequence);
    return false;
  };

  const handleStreamStart = (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    closedStreamIdsRef.current.delete(messageId);
    contentClosedStatusRef.current.delete(messageId);
    markStreamState(messageId, {
      hasContent: false,
      hasError: false,
      closed: false,
      terminalHandled: false,
    });
    dispatch(setHistoryMetadata(null));
    dispatch(setTaskStatusMessage(null));
  };

  const handleStreamEnd = async (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = ensureStreamState(messageId);
    if (streamState?.terminalHandled) {
      releaseStreamResources(messageId);
      streamStateRef.current.delete(messageId);
      return;
    }
    await finalizeRequest(messageId);
  };

  const handleTextChunk = async (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = streamStateRef.current.get(messageId);
    if (streamState?.closed) return;
    ensureStreamState(messageId);
    ensureFirstTextSegment(messageId);
    if (payload.text !== undefined) {
      await enqueueTextAppend(messageId, payload.text);
    }
  };

  const handleAssumption = (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = streamStateRef.current.get(messageId);
    if (streamState?.closed) return;
    const assumptionText = payload.assumption || payload.text || "";
    if (String(assumptionText).trim().length > 0) {
      markRenderableContentReceived(messageId);
    }
    dispatch(
      addMessage({
        type: "Harris",
        message_type: "assumption",
        message_id: messageId,
        payload: { assumption: assumptionText, message_id: messageId },
      }),
    );
    createNewTextSegment(messageId);
  };

  const handleSources = (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = streamStateRef.current.get(messageId);
    if (streamState?.closed) return;
    if (Array.isArray(payload.sources) && payload.sources.length > 0) {
      markRenderableContentReceived(messageId);
    }
    dispatch(
      addMessage({
        type: "Harris",
        message_type: "sources",
        message_id: messageId,
        payload: { sources: payload.sources || [], message_id: messageId },
      }),
    );
    createNewTextSegment(messageId);
  };

  const handleChart = (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = streamStateRef.current.get(messageId);
    if (streamState?.closed) return;
    markRenderableContentReceived(messageId);
    dispatch(
      addMessage({
        type: "Harris",
        message_type: "chart",
        message_id: messageId,
        payload: { chartConfig: payload.chartConfig || {}, message_id: messageId },
      }),
    );
    createNewTextSegment(messageId);
  };

  const handleTable = (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = streamStateRef.current.get(messageId);
    if (streamState?.closed) return;
    markRenderableContentReceived(messageId);
    dispatch(
      addMessage({
        type: "Harris",
        message_type: "table",
        message_id: messageId,
        payload: { tableConfig: payload.tableConfig || {}, message_id: messageId },
      }),
    );
    createNewTextSegment(messageId);
  };

  const handleHistoryMetadata = (payload) => {
    if (payload.message_id && !isActiveRequestMessage(payload.message_id)) return;
    const streamState = streamStateRef.current.get(payload.message_id);
    if (streamState?.closed) return;
    if (payload.metadata !== undefined) {
      dispatch(setHistoryMetadata(payload.metadata));
    }
  };

  const handleLink = (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = streamStateRef.current.get(messageId);
    if (streamState?.closed) return;
    if (messageId) {
      markRenderableContentReceived(messageId);
    }
    dispatch(
      addMessage({
        type: "Harris",
        message_type: "link",
        message_id: messageId,
        payload: {
          title: payload.title || "Link",
          link: payload.link,
          description: payload.description || payload.text || "",
          message_id: messageId,
        },
      }),
    );
  };

  const handleCustom = (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    const streamState = streamStateRef.current.get(messageId);
    if (streamState?.closed) return;
    if (messageId) {
      markRenderableContentReceived(messageId);
    }
    const customData = payload.raw?.data ?? payload.raw;
    dispatch(
      addMessage({
        type: "Harris",
        message_type: "custom",
        message_id: messageId,
        payload: {
          data: customData,
          text: typeof customData === "string" ? customData : JSON.stringify(customData),
          message_id: messageId,
        },
      }),
    );
  };

  const handleError = async (payload) => {
    const messageId = payload.message_id;
    if (!messageId || !isActiveRequestMessage(messageId)) return;
    const streamState = streamStateRef.current.get(messageId);
    if (streamState?.closed) return;
    await finalizeRequest(messageId);
  };

  const handleTerminal = async (payload) => {
    const messageId = payload.message_id;
    if (!isActiveRequestMessage(messageId)) return;
    await finalizeRequest(messageId);
  };

  const handleNavigate = (payload) => {
    if (!shouldAutoOpenNavigationLinks) return;

    const navLink = payload.link ?? payload?.raw?.payload?.link;
    if (!navLink) return;

    try {
      if (navLink?.startsWith("http")) {
        const url = new URL(navLink);
        const isSameOrigin = url.origin === window.location.origin;
        if (isSameOrigin) {
          navigate(url.pathname + url.search);
        } else {
          window.location.href = navLink;
        }
      } else {
        navigate(navLink);
      }
    } catch (error) {
      navigate(navLink);
    }
  };

  const handleSegmentsChanged = (payload) => {
    const eventMarketIds = getEventMarketIds(payload?.raw || {});
    const { marketIdsToRefresh, shouldRefresh } = resolveMarketRefresh(
      eventMarketIds,
      selectedMarketIds,
    );
    if (!shouldRefresh) return;
    const marketCodes = marketIdsToRefresh
      .map((id) => marketsById?.[id]?.identity)
      .filter(Boolean);
    dispatch(fetchSegmentsByMarketId({ marketIds: marketIdsToRefresh, marketCodes }));
  };

  useEffect(() => {
    if (
      !ablyClient?.connection ||
      typeof ablyClient.connection.on !== "function" ||
      typeof ablyClient.connection.off !== "function"
    ) {
      return undefined;
    }
    const handleConnectionState = (stateChange) => {
      const nextState = stateChange?.current || stateChange?.currentState || stateChange;
      if (!nextState || nextState === lastConnectionStateRef.current) {
        return;
      }
      lastConnectionStateRef.current = nextState;
      if (nextState === "disconnected" || nextState === "suspended") {
        emitAssistantToast({
          type: AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.WARNING,
          title: AI_ASSISTANT_CONSTANTS.TOASTS.TITLES.CONNECTION_UNSTABLE,
          description: AI_ASSISTANT_CONSTANTS.TOASTS.DESCRIPTIONS.CONNECTION_UNSTABLE,
          compact: true,
          showClose: false,
          badge: "5s",
          duration: 5000,
        });
        return;
      }
      if (nextState === "failed") {
        pushAssistantErrorMessage(AI_ASSISTANT_CONSTANTS.ERRORS.REALTIME_CONNECTION_FAILED);
        dispatch(setTaskStatusMessage(null));
        dispatch(assistantResponding(false));
        dispatch(clearActiveRequest());
        return;
      }
    };

    const watchedConnectionStates = ["connected", "disconnected", "suspended", "failed"];
    watchedConnectionStates.forEach((state) => {
      ablyClient.connection.on(state, handleConnectionState);
    });
    const initialConnectionState = ablyClient.connection.state;
    if (initialConnectionState) {
      handleConnectionState({ current: initialConnectionState });
    }
    return () => {
      watchedConnectionStates.forEach((state) => {
        ablyClient.connection.off(state, handleConnectionState);
      });
    };
  }, [ablyClient]);

  useEffect(() => {
    if (!ablyClient || !clientId) return undefined;
    const channel = getChannel(
      ablyClient,
      clientId,
      SocketChannelNamespaceType.AI_ASSISTANT,
    );
    if (!channel) return undefined;

    const handleStatus = (message) => {
      const statusPayload = parseStatusMessage(message);
      if (!statusPayload) return;
      const statusType = statusPayload.type || "task_status";
      const statusMessageId = getActiveRequestId();
      if (!statusMessageId) return;
      if (statusType === "task_status_end") {
        dispatch(setTaskStatusMessage(null));
        contentClosedStatusRef.current.add(statusMessageId);
        return;
      }
      if (statusMessageId && contentClosedStatusRef.current.has(statusMessageId)) {
        return;
      }
      dispatch(
        setTaskStatusMessage(
          buildStatusUpdate({ ...statusPayload, message_id: statusMessageId }),
        ),
      );
    };

    const handlers = {
      stream_start: handleStreamStart,
      stream_end: handleStreamEnd,
      text: handleTextChunk,
      data: handleTextChunk,
      assumption: handleAssumption,
      sources: handleSources,
      chart: handleChart,
      table: handleTable,
      history_metadata: handleHistoryMetadata,
      link: handleLink,
      custom: handleCustom,
      custom_type: handleCustom,
      error: handleError,
      terminal: handleTerminal,
      navigate: handleNavigate,
      segments_changed: handleSegmentsChanged,
    };

    const handleAIMessage = async (message) => {
      const payload = parseAIMessageData(message);
      if (!payload) return;
      if (REQUEST_SCOPED_EVENT_TYPES.has(payload.type)) {
        if (!isActiveRequestMessage(payload.message_id)) return;
      }
      if (payload.message_id && closedStreamIdsRef.current.has(payload.message_id)) {
        return;
      }
      if (shouldSkipAIMessage(payload, conversationId, shouldIgnoreSequence)) {
        return;
      }
      await runHandler(handlers[payload.type], payload);
    };

    channel.subscribe(AIAssistantEventType.ASSISTANT_TASK_STATUS, handleStatus);
    channel.subscribe(AIAssistantEventType.AI_MESSAGE, handleAIMessage);

    return () => {
      channel.unsubscribe(
        AIAssistantEventType.ASSISTANT_TASK_STATUS,
        handleStatus,
      );
      channel.unsubscribe(AIAssistantEventType.AI_MESSAGE, handleAIMessage);
    };
  }, [
    ablyClient,
    clientId,
    conversationId,
    dispatch,
    navigate,
    shouldAutoOpenNavigationLinks,
    selectedMarketIds,
    marketsById,
  ]);

  return null;
};

export default AssistantSocketListener;
